import axios from 'axios';
import { get } from 'lodash';
import { takeLatest, put, select, call, takeEvery, all } from 'redux-saga/effects';
import { message } from 'antd';

import { createDimension, buildDimensionQuery, getDimensionWidget } from './helper';
import { getDimensionData } from '@api/dimensionContainer';

import { filterSelector, intervalSelector, selectedKPIConfigSelector } from '@store/overviewDashboard/base/selectors';
import * as actions from './actions';
import * as types from './types';
import * as baseTypes from '../base/types';
import { swapSelectedDimensions, setSelectedDimension } from '@store/overviewDashboard/base/actions';
import dimensionStoreSelector, { dimensionTableColumnsSelector } from './selectors';
import {selectedThresholdConfigSelector} from '../spiThresholds/selectors';

function* initRequest() {
  yield put(actions.init());
}

// initialize and create a dimension API state
function* initDimension(action) {
  const { dimension } = action;
  const dimensionStore = createDimension(dimension);
  yield put(actions.setupDimension({ dimension, dimensionStore }));
  yield fetchDimensionData({ dimension });
}

// fetch dimension data
function* fetchDimensionData(params) {
  const { dimension } = params;
  const appliedFilter = yield select(filterSelector);
  const interval = yield select(intervalSelector);
  const columns = yield select(dimensionTableColumnsSelector);
  const kpiConfig = yield select(selectedKPIConfigSelector);

  const query = buildDimensionQuery({
    filter: appliedFilter.rule,
    interval: `${interval.from}/${interval.to}`,
    dimension,
    columns,
    kpiConfig
  });

  try {
    yield put(actions.setLoadingState(dimension, true));
    const source = axios.CancelToken.source();
    const response = yield call(
      getDimensionData,
      { queries: [query] },
      { cancelToken: source.token }
    );
    yield put(actions.setResponse({
      dimension,
      response: get(response, 'data[0]', null),
      errorMsg: '',
    }));
  } catch (error) {
    const errorMsg = error.message || 'Unknown error!';
    message.error(error.message || 'Unknown error!');
    // TODO: set success, valid error messages?
    yield put(actions.setResponse({dimension, response: null, errorMsg}));
  }
}

// set/swap dimensions
function* setDimension(action) {
  const { widgetIndex, dimensionIndex, dimension } = action;
  const dimensionWidget = yield getDimensionWidget(widgetIndex);
  const currentDimension = dimensionWidget.dimensions[dimensionIndex];
  // check if new dimension is already one of the current dimension
  const swapIndex = dimensionWidget.dimensions.indexOf(dimension);
  if (swapIndex > -1) {
    // swap dimensions
    yield put(swapSelectedDimensions(widgetIndex, dimensionIndex, dimension, swapIndex, currentDimension));
  } else {
    // set/unset dimensions
    yield put(setSelectedDimension(widgetIndex, dimensionIndex, dimension));
    yield put(actions.unsetDimension(currentDimension));
  }
}

// update data of all dimensions
// done on filter, interval or metric change
function* refreshDimensions() {
  const dimensionStore = yield select(dimensionStoreSelector);
  const tasks = Object.keys(dimensionStore).map(
    dimension =>
      call(fetchDimensionData, { dimension })
  );
  yield all(tasks);
}

function* watchInitRequest() {
  yield takeEvery([
    types.INIT,
  ], initRequest);
}

function* watchInitDimension() {
  yield takeEvery(types.INIT_DIMENSION, initDimension);
}

function* watchSetDimension() {
  yield takeEvery(types.SET_DIMENSION, setDimension);
}

function* watchSelectedKpiMetric() {
  yield takeLatest([
    baseTypes.SET_SELECTED_KPI,
    baseTypes.APPLY_FILTER,
    baseTypes.SET_INTERVAL,
    baseTypes.SET_SELECTED_KPI_CONFIG
  ], refreshDimensions);
}

// used for reload/retry
function* WatchFetchData() {
  yield takeEvery(types.FETCH_DATA, fetchDimensionData);
}

export default [
  watchInitRequest,
  watchInitDimension,
  watchSetDimension,
  watchSelectedKpiMetric,
  WatchFetchData,
];
